import React, { Component } from 'react';
import { Button } from 'hfs-common-ui';
import { connect } from 'react-redux';
import checkUseLoggingVariable from '~/utils/env-helpers/check-use-logging-variable';
import FormattedMessageWrapper from '~/components/views/FormattedMessageWrapper/FormattedMessageWrapper';
import type { DefaultMessages } from '~/i18n/types';
import type { AppState } from '~/reducers';
import * as S from './styled';

export interface Props {
  errorLangKey?: keyof DefaultMessages;
  showReloadButton?: boolean;
  onReloadClick?(): void;
  /** Called once when a child throws, e.g. to report the error. */
  onError?(error: Error, errorInfo: React.ErrorInfo): void;
  /** Clears a caught error when it changes, so the children render again without a remount. */
  resetKey?: string;
  onWebViewInitialized?: boolean;
  children?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/** Unconnected boundary; the default export adds the WebView reload label from the store. */
export class ErrorBoundary extends Component<Props, State> {
  static readonly defaultProps = {
    errorLangKey: 'web.error.something_went_wrong',
    showReloadButton: true,
  };

  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      hasError: true,
      error,
      errorInfo,
    });
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  onReloadClick = () => {
    if (this.props.onReloadClick) {
      this.props.onReloadClick();
    } else {
      document.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <S.ErrorBoundaryContainer>
        <S.ErrorBoundaryWrapper>
          <FormattedMessageWrapper
            langKey={this.props.errorLangKey || 'web.error.something_went_wrong'}
          />

          {this.props.showReloadButton && (
            <Button variant="surface" size="small" onClick={this.onReloadClick}>
              <FormattedMessageWrapper
                langKey={
                  this.props.onWebViewInitialized
                    ? 'web.error_boundary.button.reload_view'
                    : 'web.error_boundary.button.reload_page'
                }
              />
            </Button>
          )}
          {checkUseLoggingVariable() && this.state.error && (
            <pre>
              {this.state.error.message}
              {this.state.errorInfo?.componentStack && (
                <>
                  <br />
                  {this.state.errorInfo.componentStack.split('\n').slice(1, 5).join('\n')}
                </>
              )}
            </pre>
          )}
        </S.ErrorBoundaryWrapper>
      </S.ErrorBoundaryContainer>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  onWebViewInitialized: state.native.onWebViewInitialized,
});

export default connect(mapStateToProps)(ErrorBoundary);
